import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  toast,
} from '@/components/ui';
import { useCreateKakaoChannelMutation } from '../hooks/useIntegrationQuery';

export interface RegisterKakaoChannelModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RegisterKakaoChannelModal = ({ open, onOpenChange }: RegisterKakaoChannelModalProps) => {
  const { t } = useTranslation('notifications');
  const [channelName, setChannelName] = useState('');
  const [searchId, setSearchId] = useState('');
  const [senderKey, setSenderKey] = useState('');
  const [category, setCategory] = useState('');
  const [channelUrl, setChannelUrl] = useState('');
  const create = useCreateKakaoChannelMutation();

  useEffect(() => {
    if (!open) {
      setChannelName('');
      setSearchId('');
      setSenderKey('');
      setCategory('');
      setChannelUrl('');
    }
  }, [open]);

  const canSubmit =
    channelName.trim().length > 0 && searchId.trim().length > 0 && senderKey.trim().length > 0;

  const onSubmit = () => {
    create.mutate(
      {
        channelName: channelName.trim(),
        searchId: searchId.trim(),
        senderKey: senderKey.trim(),
        category: category.trim() || null,
        channelUrl: channelUrl.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success(t('integration.kakao.toast.createSuccess', '카카오 채널이 등록되었습니다.'));
          onOpenChange(false);
        },
        onError: (e) => toast.error(e.message ?? t('integration.kakao.toast.createFail', '카카오 채널 등록에 실패했습니다.')),
      },
    );
  };

  const field = (label: string, node: React.ReactNode) => (
    <div className="flex flex-col gap-1.5">
      <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
        {label}
      </span>
      {node}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('integration.kakao.register.title', '카카오 채널 등록')}</DialogTitle>
          <DialogDescription>
            {t('integration.kakao.description', '알림톡 발송에 사용할 카카오 채널(발신프로필)을 등록합니다.')}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex flex-col gap-4">
          {field(
            t('integration.kakao.register.channelName', '채널명'),
            <Input
              size="md"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              placeholder={t('integration.kakao.register.channelNamePlaceholder', '예: 에버라인')}
              aria-label={t('integration.kakao.register.channelName', '채널명')}
            />,
          )}
          {field(
            t('integration.kakao.register.searchId', '검색용 아이디'),
            <Input
              size="md"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              placeholder={t('integration.kakao.register.searchIdPlaceholder', '@everline')}
              aria-label={t('integration.kakao.register.searchId', '검색용 아이디')}
            />,
          )}
          {field(
            t('integration.kakao.register.senderKey', '발신프로필 키(senderKey)'),
            <>
              <Input
                size="md"
                className="font-mono"
                value={senderKey}
                onChange={(e) => setSenderKey(e.target.value)}
                aria-label={t('integration.kakao.register.senderKey', '발신프로필 키(senderKey)')}
              />
              <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                {t('integration.kakao.register.senderKeyHint', '비즈톡 발신프로필에서 발급받은 senderKey 를 입력하세요.')}
              </span>
            </>,
          )}
          {field(
            t('integration.kakao.register.category', '카테고리'),
            <Input
              size="md"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-label={t('integration.kakao.register.category', '카테고리')}
            />,
          )}
          {field(
            t('integration.kakao.register.channelUrl', '채널 URL'),
            <Input
              size="md"
              type="url"
              value={channelUrl}
              onChange={(e) => setChannelUrl(e.target.value)}
              placeholder="https://"
              aria-label={t('integration.kakao.register.channelUrl', '채널 URL')}
            />,
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t('common.cancel', '취소')}
          </Button>
          <Button size="sm" disabled={!canSubmit || create.isPending} onClick={onSubmit}>
            {create.isPending
              ? t('integration.kakao.register.submitting', '등록 중…')
              : t('integration.kakao.register.submit', '등록')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
